import { supabase } from '@/lib/supabase';

export interface Enrollment {
  id: string;
  user_id: string;
  course_id: string;
  batch_id: string | null;
  enrolled_at: string;
  status: string;
  progress_percentage: number;
  payment_transaction_id: string | null;
  courses?: {
    id: string;
    title: string;
    description: string;
    duration_weeks: number;
    price: number;
    thumbnail_url: string | null;
    instructor_id: string;
  };
  course_batches?: {
    id: string;
    batch_name: string;
    start_date: string;
    end_date: string;
    schedule_info: any;
  };
  payment_transactions?: {
    id: string;
    status: string;
    amount: number;
    verified_at: string | null;
  };
}

export const enrollmentService = {
  async getUserEnrollments(userId: string): Promise<Enrollment[]> {
    console.log('🔍 [EnrollmentService] Fetching enrollments for user:', userId);
    
    const { data, error } = await supabase
      .from('course_enrollments')
      .select(`
        id,
        user_id,
        course_id,
        batch_id,
        enrolled_at,
        status,
        progress_percentage,
        payment_transaction_id,
        courses (
          id,
          title,
          description,
          duration_weeks,
          price,
          thumbnail_url,
          instructor_id
        ),
        payment_transactions (
          id,
          status,
          amount,
          verified_at
        )
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('enrolled_at', { ascending: false });

    if (error) {
      console.error('❌ [EnrollmentService] Error fetching enrollments:', error);
      throw error;
    }

    console.log('✅ [EnrollmentService] Found enrollments:', data?.length || 0);
    console.log('📋 [EnrollmentService] Enrollment details:', JSON.stringify(data, null, 2));

    // Fetch instructor details for courses
    if (data && data.length > 0) {
      const instructorIds = data
        .filter(enrollment => enrollment.courses?.instructor_id)
        .map(enrollment => enrollment.courses!.instructor_id);

      if (instructorIds.length > 0) {
        const { data: instructors, error: instructorError } = await supabase
          .from('user_profiles')
          .select('id, full_name, avatar_url')
          .in('id', instructorIds);

        if (!instructorError && instructors) {
          // Map instructors to enrollments
          data.forEach(enrollment => {
            if (enrollment.courses?.instructor_id) {
              const instructor = instructors.find(
                i => i.id === enrollment.courses!.instructor_id
              );
              if (instructor && enrollment.courses) {
                (enrollment.courses as any).instructor_name = instructor.full_name;
              }
            }
          });
        }
      }

      // Fetch batch information separately if batch_id exists
      const batchIds = data
        .filter(enrollment => enrollment.batch_id)
        .map(enrollment => enrollment.batch_id!);

      if (batchIds.length > 0) {
        const { data: batches, error: batchError } = await supabase
          .from('course_batches')
          .select('id, batch_name, start_date, end_date, schedule_info')
          .in('id', batchIds);

        if (!batchError && batches) {
          // Map batches to enrollments
          data.forEach((enrollment: any) => {
            if (enrollment.batch_id) {
              const batch = batches.find(b => b.id === enrollment.batch_id);
              if (batch) {
                enrollment.course_batches = batch;
              }
            }
          });
        }
      }
    }

    return data || [];
  },

  async getEnrollmentWithZoomLink(enrollmentId: string) {
    const { data, error } = await supabase
      .from('course_enrollments')
      .select(`
        id,
        batch_id,
        course_batches (
          id,
          batch_name
        )
      `)
      .eq('id', enrollmentId)
      .single();

    if (error) throw error;
    return data;
  },

  async createEnrollment(enrollment: {
    user_id: string;
    course_id: string;
    batch_id: string | null;
    payment_transaction_id: string;
  }) {
    const { data, error } = await supabase
      .from('course_enrollments')
      .insert([
        {
          ...enrollment,
          enrolled_at: new Date().toISOString(),
          status: 'pending', // Will be activated by trigger when payment is verified
          progress_percentage: 0,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

/**
 * Get user's enrollments with batch information
 */
export async function getUserEnrollmentsWithBatches(userId: string) {
  try {
    const { data, error } = await supabase
      .from('course_enrollments')
      .select(`
        *,
        course:courses (
          id,
          title,
          instructor_name:user_profiles!courses_instructor_id_fkey(full_name),
          thumbnail_url,
          duration_weeks
        ),
        batch:course_batches!course_enrollments_batch_id_fkey (
          id,
          batch_name,
          start_date,
          end_date,
          schedule_info,
          is_active
        ),
        payment:payment_transactions!course_enrollments_payment_transaction_id_fkey (
          id,
          status,
          amount,
          verified_at
        )
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ [EnrollmentService] Error fetching enrollments:', error);
      return { data: null, error };
    }

    // Filter out enrollments with pending payments
    const verifiedEnrollments = data?.filter((enrollment: any) => {
      const paymentStatus = enrollment.payment?.status;
      // Only show enrollments with verified payments
      return paymentStatus === 'verified';
    }) || [];

    console.log('✅ [EnrollmentService] Fetched enrollments:', {
      totalCount: data?.length || 0,
      verifiedCount: verifiedEnrollments.length,
      withBatches: verifiedEnrollments.filter((e: any) => e.batch).length || 0
    });

    return { data: verifiedEnrollments, error: null };
  } catch (error: any) {
    console.error('❌ [EnrollmentService] Unexpected error:', error);
    return { data: null, error };
  }
}